import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  collection, getDocs, doc as fsDoc, getDoc, query, where, orderBy, limit,
  Timestamp, onSnapshot,
} from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  sendPushNotification, bulkNotify, hasCredentials, personalize,
  type SendNotificationArgs, type BulkNotifyResult,
} from '../query/notificationService';

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  bg: '#0E1512', card: '#17201B', cardAlt: '#1E2B22', panel: '#0A1712',
  primary: '#21808D', teal: '#4ECDC4', green: '#21C98E',
  muted: '#6A8A70', border: 'rgba(255,255,255,0.08)', text: '#E2EDE6',
  panelBorder: 'rgba(33,128,141,0.18)',
  danger: '#ef4444', warning: '#f59e0b', amber: '#f59e0b',
};

// ── Types ──────────────────────────────────────────────────────────────────────
interface UserRecord {
  docId: string;
  userId?: string;
  username?: string | null;
  phone?: string;
  fcmToken?: string;
  notactive?: boolean;
  walletBalance?: number;
  orderCount?: number;
  createdTimestamp?: any;
  lastOpened?: any;
  lastOpenedAt?: any;
  lastActive?: any;
  geohash?: string;
  location?: { latitude?: number; longitude?: number };
}

type TargetMode = 'all_users' | 'filtered_users' | 'single_user';
type Tab = 'compose' | 'history';

interface NotifLog {
  id: string;
  title: string;
  body: string;
  target: string;
  sent: number;
  failed: number;
  skipped: number;
  sentAt: any;
  imageUrl?: string;
}

// ── Geo helpers ───────────────────────────────────────────────────────────────
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const userName = (u: UserRecord) => u.username || '';

// ── Image compression (canvas, target ≤100 KB) ───────────────────────────────
async function compressToJpeg(file: File, targetKb = 100): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX) { height = Math.round(height * MAX / width); width = MAX; }
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      let quality = 0.85;
      const attempt = (q: number) => {
        canvas.toBlob(blob => {
          if (!blob) { reject(new Error('Compression failed')); return; }
          if (blob.size <= targetKb * 1024 || q <= 0.1) { resolve(blob); }
          else { attempt(parseFloat((q - 0.1).toFixed(1))); }
        }, 'image/jpeg', q);
      };
      attempt(quality);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function NotificationCenter({ branchId, branchLat: initLat, branchLng: initLng }: { branchId: string; branchLat?: number; branchLng?: number }) {
  const [tab, setTab] = useState<Tab>('compose');
  const [hasCreds, setHasCreds] = useState<boolean | null>(null);

  // Branch location (from prop or fetched once)
  const [branchLat, setBranchLat] = useState<number | null>(initLat ?? null);
  const [branchLng, setBranchLng] = useState<number | null>(initLng ?? null);

  // Target
  const [targetMode, setTargetMode] = useState<TargetMode>('all_users');
  const [singlePhone, setSinglePhone] = useState('');
  const [singleUser, setSingleUser] = useState<UserRecord | null>(null);
  const [singleSearching, setSingleSearching] = useState(false);
  const [singleError, setSingleError] = useState('');

  // User filters
  const [fStatus, setFStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [fHasFcm, setFHasFcm] = useState(true);
  const [fOrderMin, setFOrderMin] = useState('');
  const [fOrderMax, setFOrderMax] = useState('');
  const [fWalletMin, setFWalletMin] = useState('');
  const [fWalletMax, setFWalletMax] = useState('');
  const [fLastActiveDays, setFLastActiveDays] = useState('');
  const [fJoinedAfter, setFJoinedAfter] = useState('');
  const [fJoinedBefore, setFJoinedBefore] = useState('');
  const [fPhonePrefix, setFPhonePrefix] = useState('');
  const [fNameContains, setFNameContains] = useState('');
  const [fGeoEnabled, setFGeoEnabled] = useState(false);
  const [fGeoRadius, setFGeoRadius] = useState('5');

  // Compose
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [deepScreen, setDeepScreen] = useState('home');

  // Banner upload
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Preview / send
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; sent: number; failed: number } | null>(null);
  const [result, setResult] = useState<BulkNotifyResult | null>(null);
  const [sendError, setSendError] = useState('');

  // History
  const [history, setHistory] = useState<NotifLog[]>([]);

  // All users cache
  const usersCache = useRef<UserRecord[]>([]);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    hasCredentials().then(setHasCreds);
  }, []);

  useEffect(() => {
    if (!branchId || (initLat && initLng)) return;
    getDoc(fsDoc(db, 'zapee_branches', branchId)).then(snap => {
      if (!snap.exists()) return;
      const loc = snap.data()?.location ?? {};
      if (loc.latitude) setBranchLat(parseFloat(loc.latitude));
      if (loc.longitude) setBranchLng(parseFloat(loc.longitude));
    });
  }, [branchId, initLat, initLng]);

  useEffect(() => {
    if (tab !== 'history') return;
    const unsub = onSnapshot(
      query(collection(db, 'notification_logs'), orderBy('sentAt', 'desc'), limit(50)),
      snap => setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() } as NotifLog))),
      () => {},
    );
    return () => unsub();
  }, [tab]);

  // ── Banner upload ─────────────────────────────────────────────────────────
  const handleBannerUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const compressed = await compressToJpeg(file, 100);
      const storageRef = ref(storage, `notification_banners/${Date.now()}.jpg`);
      await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      setImageUrl(url);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (bannerInputRef.current) bannerInputRef.current.value = '';
    }
  }, []);

  // ── Load caches ──────────────────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    if (usersCache.current.length > 0) return usersCache.current;
    const snap = await getDocs(collection(db, 'users'));
    const list: UserRecord[] = snap.docs.map(d => ({ docId: d.id, ...d.data() } as UserRecord));
    usersCache.current = list;
    return list;
  }, []);

  // ── Filter users ──────────────────────────────────────────────────────────
  function filterUsers(all: UserRecord[]): UserRecord[] {
    return all.filter(u => {
      if (fHasFcm && !u.fcmToken) return false;
      if (fStatus === 'active' && u.notactive) return false;
      if (fStatus === 'inactive' && !u.notactive) return false;

      if (fOrderMin && (u.orderCount ?? 0) < parseInt(fOrderMin)) return false;
      if (fOrderMax && (u.orderCount ?? 0) > parseInt(fOrderMax)) return false;

      if (fWalletMin && (u.walletBalance ?? 0) < parseFloat(fWalletMin)) return false;
      if (fWalletMax && (u.walletBalance ?? 0) > parseFloat(fWalletMax)) return false;

      if (fLastActiveDays) {
        const days = parseInt(fLastActiveDays);
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - days);
        const raw = u.lastOpened ?? u.lastOpenedAt ?? u.lastActive;
        let lastDate: Date | null = null;
        if (raw) {
          lastDate = raw?.toDate ? raw.toDate() : new Date(typeof raw === 'number' ? raw : String(raw));
        }
        if (!lastDate || isNaN(lastDate.getTime()) || lastDate < cutoff) return false;
      }

      if (fJoinedAfter) {
        const after = new Date(fJoinedAfter);
        let joined: Date | null = null;
        if (u.createdTimestamp) {
          joined = u.createdTimestamp?.toDate ? u.createdTimestamp.toDate() : new Date(u.createdTimestamp);
        }
        if (!joined || joined < after) return false;
      }

      if (fJoinedBefore) {
        const before = new Date(fJoinedBefore);
        let joined: Date | null = null;
        if (u.createdTimestamp) {
          joined = u.createdTimestamp?.toDate ? u.createdTimestamp.toDate() : new Date(u.createdTimestamp);
        }
        if (!joined || joined > before) return false;
      }

      if (fPhonePrefix && !u.phone?.startsWith(fPhonePrefix)) return false;

      if (fNameContains && !userName(u).toLowerCase().includes(fNameContains.toLowerCase())) return false;

      if (fGeoEnabled && branchLat != null && branchLng != null) {
        const lat = typeof u.location?.latitude === 'number' ? u.location.latitude : parseFloat(String(u.location?.latitude ?? ''));
        const lng = typeof u.location?.longitude === 'number' ? u.location.longitude : parseFloat(String(u.location?.longitude ?? ''));
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) return false;
        const dist = haversineKm(branchLat, branchLng, lat, lng);
        if (dist > (parseFloat(fGeoRadius) || 5)) return false;
      }

      return true;
    });
  }

  // ── Single user lookup ────────────────────────────────────────────────────
  const searchSingleUser = useCallback(async () => {
    if (!singlePhone.trim()) return;
    setSingleSearching(true); setSingleError(''); setSingleUser(null);
    try {
      const all = await loadUsers();
      const found = all.find(u => u.phone === singlePhone.trim() || u.phone === `+91${singlePhone.trim()}`);
      if (found) { setSingleUser(found); }
      else { setSingleError('No user found with that phone number.'); }
    } catch { setSingleError('Search failed.'); }
    finally { setSingleSearching(false); }
  }, [singlePhone, loadUsers]);

  // ── Preview ───────────────────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    setPreviewing(true); setPreviewCount(null); setSendError('');
    try {
      if (targetMode === 'single_user') {
        setPreviewCount(singleUser ? 1 : 0);
      } else if (targetMode === 'all_users') {
        const all = await loadUsers();
        setPreviewCount(all.filter(u => !!u.fcmToken).length);
      } else {
        const all = await loadUsers();
        setPreviewCount(filterUsers(all).length);
      }
    } catch (e: any) { setSendError(e.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  }, [targetMode, singleUser, loadUsers, fStatus, fHasFcm, fOrderMin, fOrderMax,
      fWalletMin, fWalletMax, fLastActiveDays, fJoinedAfter, fJoinedBefore, fPhonePrefix, fNameContains,
      fGeoEnabled, fGeoRadius, branchLat, branchLng]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    if (!title.trim() || !body.trim()) { setSendError('Title and body are required.'); return; }
    setSending(true); setSendError(''); setResult(null); setProgress({ done: 0, total: 0, sent: 0, failed: 0 });

    try {
      const args: Omit<SendNotificationArgs, 'fcmToken'> = {
        title: title.trim(),
        body: body.trim(),
        imageUrl: imageUrl.trim() || undefined,
        screen: deepScreen || 'home',
      };

      if (targetMode === 'single_user') {
        if (!singleUser?.fcmToken) throw new Error('Selected user has no FCM token.');
        const name = userName(singleUser);
        await sendPushNotification({
          ...args,
          fcmToken: singleUser.fcmToken,
          title: personalize(args.title, name),
          body: personalize(args.body, name),
        });
        setResult({ sent: 1, failed: 0, skipped: 0 });

      } else {
        const all = usersCache.current.length ? usersCache.current : await loadUsers();
        const filtered = targetMode === 'all_users'
          ? all.filter(u => !!u.fcmToken)
          : filterUsers(all);
        const targets = filtered.map(u => ({ fcmToken: u.fcmToken, name: userName(u) || undefined }));
        setProgress({ done: 0, total: targets.length, sent: 0, failed: 0 });
        const res = await bulkNotify(targets, args, (done, total, sent, failed) =>
          setProgress({ done, total, sent, failed }),
        );
        setResult(res);
      }
    } catch (e: any) {
      setSendError(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  }, [title, body, imageUrl, deepScreen, targetMode, singleUser, loadUsers,
      fStatus, fHasFcm, fOrderMin, fOrderMax, fWalletMin, fWalletMax, fLastActiveDays,
      fJoinedAfter, fJoinedBefore, fPhonePrefix, fNameContains, fGeoEnabled, fGeoRadius, branchLat, branchLng]);

  // ── No credentials ────────────────────────────────────────────────────────
  if (hasCreds === false) {
    return (
      <div style={ss.noCreds}>
        <span className="material-icons-round" style={{ fontSize: 48, color: C.muted }}>notifications_off</span>
        <h2 style={{ color: C.text, margin: '16px 0 8px' }}>FCM credentials not configured</h2>
        <p style={{ color: C.muted, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
          Upload your Firebase service account JSON to{' '}
          <code style={{ backgroundColor: '#1E2B22', color: '#4ECDC4', padding: '1px 5px', borderRadius: 4 }}>
            settings/fcm_credentials.privateKeyJson
          </code>{' '}
          in Firestore to enable push notifications.
        </p>
      </div>
    );
  }

  const progressPct = progress && progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div style={ss.root}>
      {/* ── Top bar ── */}
      <div style={ss.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1 }}>notifications</span>
          <div>
            <div style={ss.topTitle}>Notification Center</div>
            <div style={ss.topSub}>Send targeted push notifications to users and riders</div>
          </div>
        </div>
        <div style={ss.tabs}>
          {(['compose', 'history'] as Tab[]).map(t => (
            <button key={t} style={{ ...ss.tabBtn, ...(tab === t ? ss.tabBtnActive : {}) }} onClick={() => setTab(t)}>
              <span className="material-icons-round" style={{ fontSize: 16 }}>
                {t === 'compose' ? 'send' : 'history'}
              </span>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {tab === 'history' ? (
        <HistoryTab logs={history} />
      ) : (
        <div style={ss.body}>
          {/* ── Left: Targeting + Filters ── */}
          <div style={ss.leftCol}>

            {/* Target mode */}
            <div style={ss.section}>
              <div style={ss.sectionTitle}>
                <span className="material-icons-round" style={ss.sectionIcon}>my_location</span>
                Target Audience
              </div>
              <div style={ss.targetGrid}>
                {([
                  { id: 'all_users', icon: 'group', label: 'All Users' },
                  { id: 'filtered_users', icon: 'filter_alt', label: 'Filtered Users' },
                  { id: 'single_user', icon: 'person', label: 'Single User' },
                ] as { id: TargetMode; icon: string; label: string }[]).map(t => (
                  <button
                    key={t.id}
                    style={{ ...ss.targetCard, ...(targetMode === t.id ? ss.targetCardActive : {}) }}
                    onClick={() => { setTargetMode(t.id); setPreviewCount(null); setResult(null); }}
                  >
                    <span className="material-icons-round" style={{ fontSize: 22, color: targetMode === t.id ? C.primary : C.muted }}>
                      {t.icon}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: targetMode === t.id ? C.text : C.muted, marginTop: 4 }}>
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Single user search ── */}
            {targetMode === 'single_user' && (
              <div style={ss.section}>
                <div style={ss.sectionTitle}>
                  <span className="material-icons-round" style={ss.sectionIcon}>search</span>
                  Find User
                </div>
                <div style={ss.row}>
                  <input
                    style={ss.input}
                    placeholder="Phone number (e.g. 9876543210)"
                    value={singlePhone}
                    onChange={e => setSinglePhone(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchSingleUser()}
                  />
                  <button style={ss.btnSm} onClick={searchSingleUser} disabled={singleSearching}>
                    {singleSearching ? '…' : 'Search'}
                  </button>
                </div>
                {singleError && <div style={ss.errorBox}>{singleError}</div>}
                {singleUser && (
                  <div style={ss.userCard}>
                    <div style={ss.userAvatar}>{(userName(singleUser) || 'U').charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={ss.userName}>{userName(singleUser) || 'No name'}</div>
                      <div style={ss.userMeta}>{singleUser.phone} · {singleUser.orderCount ?? 0} orders</div>
                      <div style={{ ...ss.userMeta, color: singleUser.fcmToken ? C.green : C.danger, marginTop: 2 }}>
                        {singleUser.fcmToken ? '✓ Has FCM token' : '✗ No FCM token'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── User filters ── */}
            {targetMode === 'filtered_users' && (
              <div style={ss.section}>
                <div style={ss.sectionTitle}>
                  <span className="material-icons-round" style={ss.sectionIcon}>tune</span>
                  User Filters
                </div>

                {/* Status + FCM */}
                <div style={ss.filterGrid}>
                  <div style={ss.filterCell}>
                    <div style={ss.filterLabel}>Status</div>
                    <ChipGroup
                      options={[{ v: 'all', l: 'All' }, { v: 'active', l: 'Active' }, { v: 'inactive', l: 'Inactive' }]}
                      value={fStatus}
                      onChange={v => setFStatus(v as any)}
                    />
                  </div>
                  <div style={ss.filterCell}>
                    <div style={ss.filterLabel}>FCM Token</div>
                    <Toggle label="Can receive" value={fHasFcm} onChange={setFHasFcm} />
                  </div>
                </div>

                {/* Order count */}
                <div style={ss.filterCell}>
                  <div style={ss.filterLabel}>Order Count</div>
                  <div style={ss.rangeRow}>
                    <input style={ss.rangeInput} type="number" placeholder="Min" value={fOrderMin} onChange={e => setFOrderMin(e.target.value)} />
                    <span style={{ color: C.muted, fontSize: 12 }}>–</span>
                    <input style={ss.rangeInput} type="number" placeholder="Max" value={fOrderMax} onChange={e => setFOrderMax(e.target.value)} />
                  </div>
                </div>

                {/* Wallet */}
                <div style={ss.filterCell}>
                  <div style={ss.filterLabel}>Wallet Balance (₹)</div>
                  <div style={ss.rangeRow}>
                    <input style={ss.rangeInput} type="number" placeholder="Min" value={fWalletMin} onChange={e => setFWalletMin(e.target.value)} />
                    <span style={{ color: C.muted, fontSize: 12 }}>–</span>
                    <input style={ss.rangeInput} type="number" placeholder="Max" value={fWalletMax} onChange={e => setFWalletMax(e.target.value)} />
                  </div>
                </div>

                {/* Joined date */}
                <div style={ss.filterCell}>
                  <div style={ss.filterLabel}>Joined Date</div>
                  <div style={ss.rangeRow}>
                    <input style={ss.rangeInput} type="date" value={fJoinedAfter} onChange={e => setFJoinedAfter(e.target.value)} />
                    <span style={{ color: C.muted, fontSize: 12 }}>–</span>
                    <input style={ss.rangeInput} type="date" value={fJoinedBefore} onChange={e => setFJoinedBefore(e.target.value)} />
                  </div>
                </div>

                {/* Last active + Phone prefix */}
                <div style={ss.filterGrid}>
                  <div style={ss.filterCell}>
                    <div style={ss.filterLabel}>Active within (days)</div>
                    <input style={ss.rangeInput} type="number" placeholder="e.g. 7" value={fLastActiveDays} onChange={e => setFLastActiveDays(e.target.value)} />
                  </div>
                  <div style={ss.filterCell}>
                    <div style={ss.filterLabel}>Phone Prefix</div>
                    <input style={ss.rangeInput} placeholder="+91" value={fPhonePrefix} onChange={e => setFPhonePrefix(e.target.value)} />
                  </div>
                </div>

                {/* Name contains */}
                <div style={ss.filterCell}>
                  <div style={ss.filterLabel}>Name Contains</div>
                  <input style={ss.rangeInput} placeholder="Part of name" value={fNameContains} onChange={e => setFNameContains(e.target.value)} />
                </div>

              </div>
            )}

            {/* ── Geo radius card (filtered_users only) ── */}
            {targetMode === 'filtered_users' && (
              <div style={ss.section}>
                <div style={ss.sectionTitle}>
                  <span className="material-icons-round" style={ss.sectionIcon}>my_location</span>
                  Near Branch
                </div>

                <div style={ss.filterCell}>
                  <Toggle
                    label={branchLat ? 'Filter by distance from branch' : 'Branch location unavailable'}
                    value={fGeoEnabled}
                    onChange={v => { if (branchLat) setFGeoEnabled(v); }}
                  />
                </div>

                {fGeoEnabled && branchLat && (
                  <div style={{ ...ss.filterCell, marginTop: 10 }}>
                    <div style={ss.filterLabel}>Radius (km)</div>
                    <input
                      style={{ ...ss.rangeInput, width: 100 }}
                      type="number" min="1" max="100"
                      value={fGeoRadius}
                      onChange={e => setFGeoRadius(e.target.value)}
                    />
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                      Matches users whose saved address is within {fGeoRadius || 5} km of branch
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right: Compose + Preview + Send ── */}
          <div style={ss.rightCol}>
            {/* Compose */}
            <div style={ss.section}>
              <div style={ss.sectionTitle}>
                <span className="material-icons-round" style={ss.sectionIcon}>edit_note</span>
                Compose Notification
              </div>

              <div style={ss.formGroup}>
                <label style={ss.label}>Title *</label>
                <div style={ss.quickFills}>
                  {[
                    'Hi {name},',
                    '😊 Miss You {name}!',
                    '☁️ Cloudy Morning Deal!',
                    '🌧️ Rainy Day Special!',
                    '☀️ Sunny Day Treat!',
                    '🎉 Special Offer!',
                    '💰 Cashback Alert',
                    '🍔 New on Menu',
                    '⏰ Limited Time',
                    '🎁 Reward Waiting',
                  ].map(t => (
                    <button key={t} style={ss.quickChip} onClick={() => setTitle(t)}>{t}</button>
                  ))}
                </div>
                <input style={ss.input} placeholder="Hey {name}! 🎉" value={title} onChange={e => setTitle(e.target.value)} maxLength={100} />
                <div style={ss.charCount}>{title.length}/100</div>
              </div>

              <div style={ss.formGroup}>
                <label style={ss.label}>Body *</label>
                <div style={ss.quickFills}>
                  {[
                    { l: 'Cloudy', t: 'It\'s a cloudy morning! ☁️ Perfect weather to stay in and enjoy hot food delivered right to your door. Order now!' },
                    { l: 'Rainy', t: 'It\'s raining outside! 🌧️ Don\'t step out — order your favourite food and we\'ll bring it hot to your door.' },
                    { l: 'Sunny', t: 'Sunny day vibes! ☀️ Treat yourself to something delicious. Check out today\'s menu and order now!' },
                    { l: 'Offer today', t: 'We have a special offer just for you today! 🎉 Open the app to grab it before it expires.' },
                    { l: 'Cashback', t: 'You\'ve earned cashback on your last order! 💰 Check your wallet in the app.' },
                    { l: 'New items', t: 'Fresh new items have been added to the menu 🍽️ Check them out now!' },
                    { l: 'Limited deal', t: 'Hurry! This deal expires soon ⏰ Open the app now to claim it.' },
                    { l: 'Miss you', t: 'Hey {name}, we miss you! 😊 It\'s been a while — come back and enjoy your favourite food. We\'ve got something special waiting for you!' },
                    { l: 'Reward', t: 'You have a reward waiting for you 🎁 Open the app to claim it!' },
                  ].map(({ l, t }) => (
                    <button key={l} style={ss.quickChip} onClick={() => setBody(t)}>{l}</button>
                  ))}
                </div>
                <textarea
                  style={{ ...ss.input, height: 80, resize: 'vertical', paddingTop: 10 } as React.CSSProperties}
                  placeholder="Your order is on the way! Track it now."
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  maxLength={300}
                />
                <div style={ss.charCount}>{body.length}/300</div>
              </div>

              <div style={ss.formGroup}>
                <label style={ss.label}>Banner Image (optional)</label>
                <input
                  ref={bannerInputRef}
                  type="file" accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleBannerUpload}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    style={{ ...ss.btnOutline, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <span className="material-icons-round" style={{ fontSize: 16 }}>
                      {uploading ? 'sync' : 'upload'}
                    </span>
                    {uploading ? 'Compressing…' : 'Upload'}
                  </button>
                  <input style={{ ...ss.input, flex: 1 }} placeholder="or paste URL" value={imageUrl} onChange={e => setImageUrl(e.target.value)} />
                </div>
                {uploadError && <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{uploadError}</div>}
                {imageUrl && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Compressed to ≤100 KB · uploaded to Storage</div>}
              </div>

              <div style={ss.formGroup}>
                <label style={ss.label}>Deep Link Screen</label>
                <select style={ss.select} value={deepScreen} onChange={e => setDeepScreen(e.target.value)}>
                  {['home', 'orders', 'profile', 'wallet', 'order_details', 'restaurants', 'categories'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Preview card */}
              {(title || body) && (() => {
                const previewName = targetMode === 'single_user' && singleUser ? userName(singleUser) : undefined;
                const previewTitle = personalize(title || 'Notification Title', previewName);
                const previewBody  = personalize(body  || 'Notification body text will appear here.', previewName);
                return (
                <div style={ss.previewCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={ss.previewLabel}>Preview</div>
                    {previewName && <div style={{ fontSize: 10, color: C.teal }}>Personalized for {previewName}</div>}
                  </div>
                  {imageUrl && (
                    <img src={imageUrl} alt="preview" style={ss.previewImg} onError={e => (e.currentTarget.style.display = 'none')} />
                  )}
                  <div style={ss.previewTitle}>{previewTitle}</div>
                  <div style={ss.previewBody}>{previewBody}</div>
                </div>
                );
              })()}
            </div>

            {/* Preview + Send */}
            <div style={ss.section}>
              <div style={ss.sectionTitle}>
                <span className="material-icons-round" style={ss.sectionIcon}>send</span>
                Preview & Send
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <button style={ss.btnOutline} onClick={handlePreview} disabled={previewing}>
                  {previewing ? 'Counting…' : 'Count Recipients'}
                </button>
                {previewCount !== null && (
                  <div style={ss.previewCount}>
                    <span className="material-icons-round" style={{ fontSize: 16 }}>people</span>
                    {previewCount.toLocaleString()} recipients
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {sending && progress && (
                <div style={ss.progressWrap}>
                  <div style={ss.progressBar}>
                    <div style={{ ...ss.progressFill, width: `${progressPct}%` }} />
                  </div>
                  <div style={ss.progressText}>
                    Sending {progress.done}/{progress.total} — {progress.sent} sent · {progress.failed} failed
                  </div>
                </div>
              )}

              {/* Result */}
              {result && !sending && (
                <div style={ss.resultBox}>
                  <span className="material-icons-round" style={{ color: C.green, fontSize: 22 }}>check_circle</span>
                  <div>
                    <div style={{ fontWeight: 700, color: C.green }}>Notifications sent!</div>
                    <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>
                      ✓ {result.sent} sent · ✗ {result.failed} failed · ⊘ {result.skipped} skipped
                    </div>
                  </div>
                </div>
              )}

              {sendError && <div style={ss.errorBox}>{sendError}</div>}

              <button
                style={{ ...ss.btnSend, ...(sending || !title || !body ? ss.btnDisabled : {}) }}
                onClick={handleSend}
                disabled={sending || !title.trim() || !body.trim()}
              >
                {sending ? (
                  <><span className="material-icons-round" style={{ fontSize: 18, animation: 'spin 1s linear infinite' }}>sync</span> Sending…</>
                ) : (
                  <><span className="material-icons-round" style={{ fontSize: 18 }}>send</span> Send Notification</>
                )}
              </button>

              <p style={ss.tipText}>
                {'{name}'} in title/body is replaced with each recipient's name.
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────
function HistoryTab({ logs }: { logs: NotifLog[] }) {
  function fmtDate(ts: any): string {
    if (!ts) return '—';
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(typeof ts === 'number' ? ts : String(ts));
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  }

  if (logs.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60, color: C.muted }}>
      <span className="material-icons-round" style={{ fontSize: 48, marginBottom: 12 }}>notifications_none</span>
      No notification history yet.
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: 900, backgroundColor: C.bg, minHeight: '100%' }}>
      {logs.map(log => (
        <div key={log.id} style={ss.histCard}>
          <div style={ss.histTop}>
            <div style={ss.histTitle}>{log.title}</div>
            <div style={ss.histDate}>{fmtDate(log.sentAt)}</div>
          </div>
          <div style={ss.histBody}>{log.body}</div>
          <div style={ss.histMeta}>
            <span style={{ color: C.muted }}>→ {log.target}</span>
            <span style={{ color: C.green }}>✓ {log.sent} sent</span>
            {log.failed > 0 && <span style={{ color: C.danger }}>✗ {log.failed} failed</span>}
            {log.skipped > 0 && <span style={{ color: C.muted }}>⊘ {log.skipped} skipped</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Small reusable components ─────────────────────────────────────────────────
function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <div style={{ minWidth: 150, fontSize: 12, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function ChipGroup({ options, value, onChange }: {
  options: { v: string; l: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(o => (
        <button
          key={o.v}
          style={{ ...ss.chip, ...(value === o.v ? ss.chipActive : {}) }}
          onClick={() => onChange(o.v)}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <div
        style={{
          width: 36, height: 20, borderRadius: 10,
          backgroundColor: value ? C.primary : '#2A3B30',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}
        onClick={() => onChange(!value)}
      >
        <div style={{
          position: 'absolute', top: 2, left: value ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%',
          backgroundColor: '#fff', transition: 'left 0.2s',
        }} />
      </div>
      <span style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>{label}</span>
    </label>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const ss: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: C.bg, overflow: 'hidden' },
  noCreds: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40, backgroundColor: C.bg },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px', backgroundColor: C.panel,
    borderBottom: `1px solid ${C.panelBorder}`, flexShrink: 0,
  },
  topTitle: { fontSize: 16, fontWeight: 800, color: '#fff' },
  topSub: { fontSize: 12, color: '#8BAAB8', marginTop: 2 },
  tabs: { display: 'flex', gap: 6 },
  tabBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(33,128,141,0.25)',
    backgroundColor: 'transparent', color: '#8BAAB8', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  tabBtnActive: { backgroundColor: 'rgba(33,128,141,0.22)', borderColor: C.primary, color: C.teal },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  leftCol: { width: 380, overflowY: 'auto', borderRight: `1px solid ${C.border}`, padding: '16px', flexShrink: 0, backgroundColor: C.bg },
  rightCol: { flex: 1, overflowY: 'auto', padding: '16px 24px', backgroundColor: C.bg },
  section: {
    backgroundColor: C.card, borderRadius: 12, padding: '18px 16px', marginBottom: 14,
    border: `1px solid ${C.border}`,
  },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 14 },
  sectionIcon: { fontSize: 18, color: C.primary },
  targetGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  targetCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '14px 10px', borderRadius: 10, border: `1px solid ${C.border}`,
    backgroundColor: C.cardAlt, cursor: 'pointer', transition: 'all 0.15s',
  },
  targetCardActive: { backgroundColor: 'rgba(33,128,141,0.18)', borderColor: C.primary },
  formGroup: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${C.border}`, fontSize: 13, color: C.text,
    backgroundColor: C.cardAlt, boxSizing: 'border-box', outline: 'none',
  },
  select: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${C.border}`, fontSize: 13, color: C.text,
    backgroundColor: C.cardAlt, boxSizing: 'border-box', outline: 'none',
  },
  charCount: { fontSize: 11, color: C.muted, textAlign: 'right', marginTop: 3 },
  previewCard: {
    backgroundColor: '#0A1712', borderRadius: 12, padding: '14px 16px', marginTop: 14,
    border: `1px solid rgba(33,128,141,0.25)`,
  },
  previewLabel: { fontSize: 10, color: '#4ECDC4', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 },
  previewImg: { width: '100%', height: 80, objectFit: 'cover', borderRadius: 8, marginBottom: 10 },
  previewTitle: { fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 4 },
  previewBody: { fontSize: 12, color: '#8BAAB8', lineHeight: 1.5 },
  previewCount: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
    backgroundColor: 'rgba(33,128,141,0.18)', borderRadius: 8, fontSize: 13, fontWeight: 700, color: C.teal,
  },
  progressWrap: { marginBottom: 14 },
  progressBar: { height: 8, backgroundColor: C.cardAlt, borderRadius: 4, overflow: 'hidden', marginBottom: 6 },
  progressFill: { height: '100%', backgroundColor: C.primary, borderRadius: 4, transition: 'width 0.3s' },
  progressText: { fontSize: 12, color: C.muted },
  resultBox: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', backgroundColor: 'rgba(33,201,142,0.1)', borderRadius: 8, border: '1px solid rgba(33,201,142,0.25)', marginBottom: 14 },
  errorBox: { padding: '10px 14px', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 13, color: '#f87171', marginBottom: 10 },
  btnSend: {
    width: '100%', height: 46, backgroundColor: C.primary, color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  btnOutline: {
    padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
    backgroundColor: C.cardAlt, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.text,
  },
  btnSm: {
    padding: '8px 14px', borderRadius: 8, backgroundColor: C.primary, color: '#fff',
    border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  tipText: { fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 10, marginBottom: 0 },
  row: { display: 'flex', gap: 8, alignItems: 'center' },
  filterGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 },
  filterCell: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 },
  filterLabel: { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  rangeRow: { display: 'flex', alignItems: 'center', gap: 6 },
  rangeInput: {
    flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 6,
    border: `1px solid ${C.border}`, fontSize: 12, color: C.text,
    backgroundColor: C.cardAlt, boxSizing: 'border-box', outline: 'none', width: '100%',
  },
  quickFills: { display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 },
  quickChip: {
    padding: '3px 9px', borderRadius: 20, border: `1px solid ${C.border}`,
    backgroundColor: C.cardAlt, color: C.text, fontSize: 11, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  chip: {
    padding: '4px 10px', borderRadius: 20, border: `1px solid ${C.border}`,
    backgroundColor: 'transparent', color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  chipActive: { backgroundColor: 'rgba(33,128,141,0.2)', borderColor: C.primary, color: C.teal },
  userCard: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    backgroundColor: C.cardAlt, borderRadius: 10, border: `1px solid ${C.border}`, marginTop: 10,
  },
  userAvatar: {
    width: 38, height: 38, borderRadius: '50%', backgroundColor: C.primary,
    color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  userName: { fontSize: 14, fontWeight: 700, color: C.text },
  userMeta: { fontSize: 12, color: C.muted, marginTop: 1 },
  histCard: {
    backgroundColor: C.card, borderRadius: 10, padding: '14px 16px',
    marginBottom: 10, border: `1px solid ${C.border}`,
  },
  histTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 },
  histTitle: { fontSize: 14, fontWeight: 700, color: C.text },
  histDate: { fontSize: 11, color: C.muted, flexShrink: 0, marginLeft: 12 },
  histBody: { fontSize: 13, color: C.muted, marginBottom: 8, lineHeight: 1.5 },
  histMeta: { display: 'flex', gap: 12, fontSize: 12, fontWeight: 600 },
};
